import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { StatusBannerAutoHide } from '@xing-com/banner';
import { useInterval } from '@xing-com/crate-hooks-use-interval';
import { usePopOver } from '@xing-com/pop-over';

import { Styled } from './resend-countdown.styles';

type ResendCountdownProps = {
  sendFn: () => void;
  status: 'success' | 'error' | null;
  initialTime?: number;
  center?: boolean;
  successMessage: MessageDescriptor;
  errorMessage: MessageDescriptor;
  sendMessage: MessageDescriptor;
  sendLinkMessage: MessageDescriptor;
  waitMessage: MessageDescriptor;
};

export const ResendCountdown: React.FC<ResendCountdownProps> = ({
  sendFn,
  status,
  initialTime = 30,
  center = false,
  successMessage,
  errorMessage,
  sendMessage,
  sendLinkMessage,
  waitMessage,
}) => {
  const [time, setTime] = useState(initialTime);
  const [isCounting, setIsCounting] = useState(true);
  const popOver = usePopOver();
  const [showBanner, setShowBanner] = useState(false);
  const handleResendClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
  ): void => {
    e.preventDefault();

    if (time <= 0) {
      setTime(initialTime);
      setIsCounting(true);
      setShowBanner(true);
      sendFn();
    } else {
      popOver.handleShow();
    }
  };

  useInterval(
    () => {
      if (time <= 0) {
        popOver.handleHide();
        setIsCounting(false);
        return;
      }

      setTime((time) => time - 1);
    },
    isCounting ? 1000 : null
  );

  return (
    <>
      {status ? (
        <StatusBannerAutoHide
          show={showBanner}
          variant={status}
          timeout={4000}
          handleOnClose={() => setShowBanner(false)}
        >
          {status === 'success' ? (
            <FormattedMessage {...successMessage} />
          ) : (
            <FormattedMessage {...errorMessage} />
          )}
        </StatusBannerAutoHide>
      ) : null}

      <Styled.TextWrapper center={center}>
        <Styled.ResendWrapper size="medium" sizeWide="large" noMargin>
          <FormattedMessage
            {...sendMessage}
            values={{
              link: (
                <Styled.ResendButton
                  onClick={handleResendClick}
                  size="medium"
                  sizeWide="large"
                >
                  <FormattedMessage {...sendLinkMessage} />
                </Styled.ResendButton>
              ),
            }}
          />
        </Styled.ResendWrapper>
        <Styled.PopOver
          data-qa="cooldown"
          isInFlow
          triggerRef={popOver.triggerRef}
          onOutsideClick={popOver.handleHide}
          show={popOver.show}
        >
          <FormattedMessage {...waitMessage} values={{ countdown: time }} />
        </Styled.PopOver>
      </Styled.TextWrapper>
    </>
  );
};
